import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Clock, Wifi, MessageSquare, Video, CheckCircle2 } from "lucide-react";
import { useClient } from "@/lib/clientContext";
import { listAppointments, isEnded } from "@/lib/telehealth";
import { openTelehealthSession } from "@/components/telehealthv2/thModel";

const KIND = {
  waiting: { icon: Clock, label: "In the waiting room", bg: "#FFF4E0", fg: "#9A5A00", dot: "#F08C00" },
  live: { icon: Wifi, label: "Visit in progress", bg: "#E7F7EC", fg: "#166534", dot: "#1F8A4C" },
  message: { icon: MessageSquare, label: "new message", bg: "#FDEBE3", fg: "#C2410C", dot: "#C2410C" },
};

const fmtClock = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); };

const STALE_MS = 2 * 60 * 60 * 1000; // 2 hours — older signals are considered stale and hidden

// Drop signals whose timestamp is older than 2h (a stale live signal). Items with a
// missing/unparseable time are kept so a genuine live signal is never hidden.
const isStale = (iso) => { const t = new Date(iso).getTime(); return !isNaN(t) && (Date.now() - t) > STALE_MS; };

function derive(appts) {
  const out = [];
  for (const a of appts || []) {
    if (isEnded(a.status)) continue;
    const name = a.patient?.name || "Patient";
    if (a.patient_waiting || a.status === "waiting_room" || a.status === "waiting")
      out.push({ id: `${a.appointment_id}-wait`, appt: a, kind: "waiting", prio: 0, name, time: a.scheduled_at });
    else if (a.status === "in_progress")
      out.push({ id: `${a.appointment_id}-live`, appt: a, kind: "live", prio: 1, name, time: a.start_time || a.scheduled_at });
    if (a.unread_count > 0)
      out.push({ id: `${a.appointment_id}-msg`, appt: a, kind: "message", prio: 2, name, count: a.unread_count, time: a.updated_at || a.scheduled_at });
  }
  const fresh = out.filter((it) => !isStale(it.time));
  fresh.sort((x, y) => new Date(y.time) - new Date(x.time)); // newest first
  return fresh;
}

// Global live-visit notification center — polls telemedicine appointments every
// 15s regardless of the current page, so waiting-room / connected / new-message
// signals surface anywhere in the app.
export default function NotificationCenter() {
  const { selectedClientId } = useClient();
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const pull = useCallback(async () => {
    if (!selectedClientId) { setAppts([]); return; }
    try { const data = await listAppointments(selectedClientId); setAppts(Array.isArray(data) ? data : (data?.appointments || [])); } catch { /* keep last */ }
  }, [selectedClientId]);

  useEffect(() => {
    pull();
    const iv = setInterval(() => { if (!document.hidden) pull(); }, 15000);
    return () => clearInterval(iv);
  }, [pull]);

  const items = useMemo(() => derive(appts), [appts]);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const count = items.length;
  const urgent = items.some((i) => i.kind === "waiting" || i.kind === "message");

  // Waiting/live alerts open that visit (same as the long-visit bar's Open); message alerts go to Telehealth.
  const go = (appt, kind) => { setOpen(false); if (kind === "message") navigate("/telehealth-v2"); else openTelehealthSession(appt); };

  return (
    <div className="relative" ref={panelRef}>
      <button onClick={() => { setOpen((v) => !v); if (!open) pull(); }} data-testid="notif-bell" aria-label={`Live visit alerts${count ? ` (${count})` : ""}`}
        className="relative w-[32px] h-[32px] rounded-[8px] flex items-center justify-center transition-colors hover:bg-black/[.05]" style={{ color: "#44443F" }}>
        <Bell size={17} strokeWidth={1.9} />
        {count > 0 ? (
          <span data-testid="notif-badge" className="absolute -top-[3px] -right-[3px] min-w-[16px] h-[16px] px-[3px] rounded-full text-white text-[10px] font-bold flex items-center justify-center" style={{ background: urgent ? "#DC2626" : "#6C6C69", boxShadow: "0 0 0 2px #fff" }}>
            {count > 9 ? "9+" : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div data-testid="notif-panel" className="absolute right-0 top-[42px] z-[60] w-[360px] max-w-[92vw] rounded-[14px] overflow-hidden"
          style={{ background: "#fff", border: "1px solid #E9E9EC", boxShadow: "0 20px 56px rgba(20,20,18,.22)" }}>
          <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid #F1F1F0" }}>
            <Video size={15} style={{ color: "#4B4BE8" }} />
            <span className="text-[13px] font-bold" style={{ color: "#141412" }}>Live visit activity</span>
            <span className="flex-1" />
            <button onClick={() => setOpen(false)} data-testid="notif-close" className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center hover:bg-black/[.05]" style={{ color: "#6C6C69" }}><X size={15} /></button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {!count ? (
              <div className="px-4 py-10 text-center" data-testid="notif-empty">
                <CheckCircle2 size={26} className="mx-auto mb-2" style={{ color: "#B6D9C1" }} />
                <div className="text-[13px] font-semibold" style={{ color: "#141412" }}>All clear</div>
                <div className="text-[12px] mt-0.5" style={{ color: "#9B9B97" }}>No patients waiting, connected or messaging right now.</div>
              </div>
            ) : (
              <div className="p-2 space-y-1.5">
                {items.map((it) => {
                  const k = KIND[it.kind];
                  const msg = it.kind === "message" ? `${it.count} new message${it.count === 1 ? "" : "s"}` : k.label;
                  return (
                    <button key={it.id} onClick={() => go(it.appt, it.kind)} data-testid={`notif-item-${it.appt.appointment_id}`}
                      className="w-full text-left flex items-center gap-2.5 rounded-[10px] px-2.5 py-2.5 transition-colors hover:bg-black/[.03]">
                      <span className="inline-flex items-center justify-center rounded-[8px] flex-none" style={{ width: 32, height: 32, background: k.bg, color: k.fg }}><k.icon size={15} /></span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-bold flex items-center gap-1.5" style={{ color: "#141412" }}>
                          <span className="w-[7px] h-[7px] rounded-full flex-none" style={{ background: k.dot }} />
                          <span className="truncate">{it.name}</span>
                        </div>
                        <div className="text-[11.5px] mt-0.5" style={{ color: k.fg }}>{msg}</div>
                      </div>
                      <span className="text-[10.5px] flex-none" style={{ color: "#9B9B97" }}>{fmtClock(it.time)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {count ? (
            <button onClick={() => { setOpen(false); navigate("/telehealth-v2"); }} data-testid="notif-view-all"
              className="w-full py-2.5 text-[12.5px] font-bold" style={{ color: "#4B4BE8", borderTop: "1px solid #F1F1F0" }}>Open Telehealth</button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
